import React, { useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import tariffService from '../../services/tariffService';

const unitsText = (r) => (r.item_type === 'surgical' ? `${Number(r.anaesthetic_units)} anaes. units` : `${Number(r.clinical_units)} clinical units`);

/**
 * TariffPicker — type a procedure or a code, pick a tariff code.
 * onPick(row) gets the tariff_codes row; the box clears afterwards.
 */
const TariffPicker = ({ onPick, placeholder = 'Add a procedure — type words or a code…', disabled = false, exclude = [] }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const text = q.trim();
    if (text.length < 2) { setResults([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const rows = await tariffService.search(text);
        if (!cancelled) { setResults(rows.filter((r) => !exclude.includes(r.code))); setActive(0); setOpen(true); }
      } catch {
        if (!cancelled) setResults([]);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
    // exclude is compared by value on purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, exclude.join(',')]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const pick = (row) => {
    onPick(row);
    setQ('');
    setResults([]);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open || !results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === 'Enter') { e.preventDefault(); pick(results[active]); }
    if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative">
      <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onKeyDown={onKey}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-300 py-2 pl-8 pr-3 text-sm focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30 disabled:bg-slate-50"
      />
      {open && q.trim().length >= 2 && (
        <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {results.map((r, i) => (
            <li key={r.code}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(r)}
                onMouseEnter={() => setActive(i)}
                className={`flex w-full items-start gap-3 px-3 py-1.5 text-left text-sm ${i === active ? 'bg-teal-50' : ''}`}
              >
                <span className="w-12 shrink-0 font-mono font-semibold text-brand-navy">{r.code}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-slate-800">{r.description}</span>
                  <span className="block text-[11px] text-slate-400">{r.section} · {unitsText(r)}</span>
                </span>
              </button>
            </li>
          ))}
          {results.length === 0 && <li className="px-3 py-2 text-xs text-slate-400">No matching tariff codes.</li>}
        </ul>
      )}
    </div>
  );
};

export default TariffPicker;
